import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { removeGiftIfCartEmpty } from './gift-with-purchase.js';

const CART_TIMER_KEY = 'cart_expiry_timestamp';
const CART_TIMER_DURATION_MS = 5 * 60 * 1000;

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;
    this.timerTimeoutId = null;
    this.timerExpiry = null;
    this.isClearingCart = false;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
    this.visibilityChangeHandler = this.#handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.#rebindCheckoutButton();

    this.addEventListener('click', this.backdropClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('pageshow', this.visibilityChangeHandler);

    this.#syncCartTimer();

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton?.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    window.removeEventListener('pageshow', this.visibilityChangeHandler);

    this.#stopTimerTick();

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #rebindCheckoutButton() {
    this.checkoutButton?.removeEventListener('click', this.checkoutButtonClickHandler);
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');
    this.checkoutButton?.addEventListener('click', this.checkoutButtonClickHandler);
  }

  #getTimerExpiry() {
    if (this.timerExpiry) {
      return this.timerExpiry;
    }

    try {
      const storedExpiry = parseInt(sessionStorage.getItem(CART_TIMER_KEY), 10);
      if (storedExpiry) {
        this.timerExpiry = storedExpiry;
        return storedExpiry;
      }
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  #setTimerExpiry(expiry) {
    this.timerExpiry = expiry;

    try {
      sessionStorage.setItem(CART_TIMER_KEY, String(expiry));
    } catch (error) {
      console.error(error);
    }
  }

  #removeTimerExpiry() {
    this.timerExpiry = null;

    try {
      sessionStorage.removeItem(CART_TIMER_KEY);
    } catch (error) {
      console.error(error);
    }
  }

  #syncCartTimer(itemCount) {
    const count = itemCount ?? this.querySelectorAll('cart-drawer-item').length;

    if (count === 0) {
      this.#clearCartTimer();
      return;
    }

    if (!this.#getTimerExpiry()) {
      this.#startCartTimer();
      return;
    }

    this.#initCartTimerDisplay();
  }

  #startCartTimer() {
    this.#setTimerExpiry(Date.now() + CART_TIMER_DURATION_MS);
    this.#initCartTimerDisplay();
  }

  #clearCartTimer() {
    this.#removeTimerExpiry();
    this.#stopTimerTick();

    const timerEl = this.querySelector('[data-ref="cart-timer"]');
    if (timerEl) {
      timerEl.hidden = true;
    }
  }

  #stopTimerTick() {
    clearTimeout(this.timerTimeoutId);
    this.timerTimeoutId = null;
  }

  #initCartTimerDisplay() {
    this.#stopTimerTick();

    const timerEl = this.querySelector('[data-ref="cart-timer"]');
    const countdownEl = this.querySelector('[data-ref="cart-timer-countdown"]');

    if (!timerEl || !countdownEl) {
      return;
    }

    const expiry = this.#getTimerExpiry();

    if (!expiry) {
      timerEl.hidden = true;
      return;
    }

    const tick = () => {
      const remaining = expiry - Date.now();

      if (remaining <= 0) {
        this.#onTimerExpired();
        return;
      }

      timerEl.hidden = false;
      countdownEl.textContent = this.#formatTime(remaining);
      this.timerTimeoutId = setTimeout(tick, 1000);
    };

    tick();
  }

  #handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      return;
    }

    this.#initCartTimerDisplay();
  }

  #formatTime(ms) {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  async #onTimerExpired() {
    if (this.isClearingCart) {
      return;
    }

    this.isClearingCart = true;
    this.#stopTimerTick();
    this.#removeTimerExpiry();

    const timerEl = this.querySelector('[data-ref="cart-timer"]');
    if (timerEl) {
      timerEl.hidden = true;
    }

    try {
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: this.dataset.sectionId,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count,
            source: 'cart-timer',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.isClearingCart = false;
    }
  }

  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.#initCartTimerDisplay();
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  renderCartContents(parsedState) {
    const section_cart_drawer_id = this.dataset.sectionId;

    const currentCartDrawer = this.querySelector('.cart-drawer__inner');
    const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[section_cart_drawer_id], 'text/html');

    currentCartDrawer.innerHTML = newCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

    this.#rebindCheckoutButton();
    this.#syncCartTimer(parsedState.itemCount);

    setTimeout(() => {
      if (parsedState.itemCount == 0) {
        this.classList.add('cart-drawer--empty');
      } else {
        this.classList.remove('cart-drawer--empty');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();

    if (this.dataset.giftItem === 'true') {
      return;
    }

    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();

    if (this.dataset.giftItem === 'true') {
      if (this.quantitySelectorInput) {
        this.quantitySelectorInput.value = 1;
      }
      return;
    }

    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.closest('cart-drawer').dataset.sectionId,
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/json` },
      ...{ body },
    })
      .then((response) => {
        return response.text();
      })
      .then(async (responsetext) => {
        let parsedResponseText = JSON.parse(responsetext);

        if (parsedResponseText.errors) {
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        const sections = this.closest('cart-drawer')?.dataset.sectionId;
        const giftCleanupResponse = await removeGiftIfCartEmpty(sections);

        if (giftCleanupResponse) {
          parsedResponseText = giftCleanupResponse;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
